import React, { useState, useEffect, useCallback } from 'react';
import { MeetingService } from '../../services/meetingService';
import { useMeetings } from '../../hooks/useMeetings';
import { useAuth } from '../../contexts/AuthContext';
import { X, Calendar, CheckSquare, Search, Check, UserX } from 'lucide-react';

interface CreateAndMarkAttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  refresh: () => void;
}

export default function CreateAndMarkAttendanceModal({ isOpen, onClose, refresh }: CreateAndMarkAttendanceModalProps) {
  const { meetingTypes, refresh: refreshMeetings } = useMeetings();
  const { user } = useAuth();
  const [step, setStep] = useState<'create' | 'attendance'>('create');
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [members, setMembers] = useState<any[]>([]);
  const [attendance, setAttendance] = useState<{ [key: string]: 'Present' | 'Absent' }>({});
  const [createdMeetingId, setCreatedMeetingId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadMembers = useCallback(async () => {
    if (!selectedMeetingType) return;

    const { data } = await MeetingService.getMembersForMeetingType(selectedMeetingType);
    if (data) {
      setMembers(data);
      // Initialize all as present by default (since they mark attendance immediately)
      const initialAttendance = data.reduce((acc, member) => ({
        ...acc,
        [member.id]: 'Present'
      }), {});
      setAttendance(initialAttendance);
    }
  }, [selectedMeetingType]);

  useEffect(() => {
    if (selectedMeetingType && step === 'attendance') {
      loadMembers();
    }
  }, [selectedMeetingType, step, loadMembers]);

  const handleCreateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!selectedMeetingType || !date) {
      setError('Please select meeting type and date');
      setLoading(false);
      return;
    }

    if (!user?.id) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    const result = await MeetingService.createInstance(
      user.id,
      selectedMeetingType,
      {
        meeting_type_id: selectedMeetingType,
        date,
        time: time || undefined
      }
    );

    if (result.data) {
      setCreatedMeetingId(result.data.id);
      setStep('attendance');
      refreshMeetings();
      refresh();
    } else {
      setError(result.error?.message || 'Failed to create meeting');
    }

    setLoading(false);
  };

  const handleSaveAttendance = async () => {
    setError('');
    setLoading(true);

    if (!createdMeetingId) {
      setError('Meeting not created');
      setLoading(false);
      return;
    }

    // Convert attendance to array format
    const attendanceData = Object.entries(attendance).map(([memberId, status]) => ({
      member_id: memberId,
      status
    }));

    if (!user?.id) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    const result = await MeetingService.markAttendance(
      createdMeetingId,
      attendanceData,
      user.id
    );

    if (result.data) {
      // Update meeting status to Completed since attendance is marked immediately
      await MeetingService.updateInstance(createdMeetingId, {
        status: 'Completed'
      });
      
      refreshMeetings();
      refresh();
      handleClose();
    } else {
      setError(result.error?.message || 'Failed to mark attendance');
    }

    setLoading(false);
  };

  const setMemberStatus = (memberId: string, status: 'Present' | 'Absent') => {
    setAttendance(prev => ({
      ...prev,
      [memberId]: status
    }));
  };

  const markAllPresent = () => {
    const allPresent = members.reduce((acc, member) => ({
      ...acc,
      [member.id]: 'Present'
    }), {});
    setAttendance(allPresent);
  };

  const markAllAbsent = () => {
    const allAbsent = members.reduce((acc, member) => ({
      ...acc,
      [member.id]: 'Absent'
    }), {});
    setAttendance(allAbsent);
  };

  const handleClose = () => {
    setStep('create');
    setSelectedMeetingType('');
    setDate('');
    setTime('');
    setMembers([]);
    setAttendance({});
    setCreatedMeetingId('');
    setSearchQuery('');
    setError('');
    refresh();
    onClose();
  };

  const getSelectedMeetingType = () => {
    return meetingTypes.find(type => type.id === selectedMeetingType);
  };

  const filteredMembers = members.filter(member =>
    member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.phone?.includes(searchQuery)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-opacity">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200">
        {/* Mobile Pull Indicator */}
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto my-2 sm:hidden shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
            {step === 'create' ? 'Create Meeting & Mark Attendance' : 'Mark Attendance'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition min-w-[40px] min-h-[40px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50/50 shrink-0">
          <div className="flex items-center">
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
              step === 'create' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
            }`}>
              {step === 'attendance' ? '✓' : '1'}
            </div>
            <div className={`flex-1 h-1 mx-2 rounded-full ${
              step === 'attendance' ? 'bg-green-600' : 'bg-gray-300'
            }`} />
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
              step === 'attendance' ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
            }`}>
              2
            </div>
            <div className="ml-2 text-xs sm:text-sm font-medium text-gray-700">
              {step === 'create' ? 'Create Meeting' : 'Mark Attendance'}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">
              {error}
            </div>
          )}

          {step === 'create' && (
            <form onSubmit={handleCreateMeeting} className="space-y-4">
              {/* Meeting Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Meeting Type
                </label>
                <select
                  value={selectedMeetingType}
                  onChange={(e) => setSelectedMeetingType(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm bg-white"
                  required
                >
                  <option value="">Select meeting type...</option>
                  {meetingTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} - {type.level}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm bg-white"
                  required
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Time (optional)
                </label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm bg-white"
                />
              </div>

              {/* Meeting Info Preview */}
              {selectedMeetingType && date && (
                <div className="p-4 bg-blue-50/70 border border-blue-200 rounded-xl">
                  <h3 className="font-semibold text-blue-900 text-sm mb-1.5">Meeting Preview</h3>
                  <div className="space-y-1 text-xs sm:text-sm text-blue-800">
                    <p><strong>Type:</strong> {getSelectedMeetingType()?.name} - {getSelectedMeetingType()?.level}</p>
                    <p><strong>Date:</strong> {date}</p>
                    {time ? <p><strong>Time:</strong> {time}</p> : null}
                    <p className="text-blue-600 italic">Attendance will be marked in step 2</p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !selectedMeetingType || !date}
                className="w-full min-h-[48px] flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 active:bg-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm text-sm sm:text-base mt-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Calendar size={18} />
                    <span>Create & Continue to Attendance</span>
                  </>
                )}
              </button>
            </form>
          )}

          {step === 'attendance' && (
            <div className="space-y-4">
              {/* Meeting Info Summary */}
              <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between text-xs sm:text-sm">
                <div>
                  <h3 className="font-bold text-gray-900">
                    {getSelectedMeetingType()?.name}
                  </h3>
                  <p className="text-gray-500">{date} {time && `• ${time}`}</p>
                </div>
                <span className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold text-xs">
                  {members.length} Members
                </span>
              </div>

              {/* Search Member Bar */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search member name or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Quick Action Buttons */}
              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-xs text-gray-500 font-medium">Quick Bulk Actions:</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={markAllPresent}
                    className="px-2.5 py-1 text-xs font-semibold bg-green-100 text-green-700 hover:bg-green-200 rounded-lg transition"
                  >
                    All Present
                  </button>
                  <button
                    type="button"
                    onClick={markAllAbsent}
                    className="px-2.5 py-1 text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition"
                  >
                    All Absent
                  </button>
                </div>
              </div>

              {/* Attendance List with Segmented Controls */}
              <div className="space-y-2 max-h-[42vh] sm:max-h-80 overflow-y-auto pr-1">
                {filteredMembers.length > 0 ? (
                  filteredMembers.map((member) => {
                    const isPresent = attendance[member.id] === 'Present';
                    return (
                      <div
                        key={member.id}
                        className={`flex items-center justify-between p-3 border rounded-xl transition ${
                          isPresent ? 'bg-green-50/40 border-green-200' : 'bg-red-50/30 border-red-100'
                        }`}
                      >
                        <div className="min-w-0 flex-1 pr-2">
                          <p className="font-semibold text-gray-900 text-xs sm:text-sm truncate">{member.name}</p>
                          <p className="text-[11px] sm:text-xs text-gray-500">{member.phone}</p>
                        </div>

                        {/* Segmented Present / Absent Buttons */}
                        <div className="flex items-center bg-gray-200 p-0.5 rounded-lg shrink-0">
                          <button
                            type="button"
                            onClick={() => setMemberStatus(member.id, 'Present')}
                            className={`flex items-center space-x-1 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                              isPresent
                                ? 'bg-green-600 text-white shadow-xs'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <Check size={14} />
                            <span>Present</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setMemberStatus(member.id, 'Absent')}
                            className={`flex items-center space-x-1 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                              !isPresent
                                ? 'bg-red-600 text-white shadow-xs'
                                : 'text-gray-600 hover:text-gray-900'
                            }`}
                          >
                            <UserX size={14} />
                            <span>Absent</span>
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-gray-500 py-6 text-xs sm:text-sm">
                    No members match your search.
                  </div>
                )}
              </div>

              {/* Summary Stats Bar */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div>
                    <p className="text-base sm:text-lg font-bold text-gray-900">{members.length}</p>
                    <p className="text-gray-500">Total</p>
                  </div>
                  <div>
                    <p className="text-base sm:text-lg font-bold text-green-600">
                      {Object.values(attendance).filter(status => status === 'Present').length}
                    </p>
                    <p className="text-gray-500">Present</p>
                  </div>
                  <div>
                    <p className="text-base sm:text-lg font-bold text-red-600">
                      {Object.values(attendance).filter(status => status === 'Absent').length}
                    </p>
                    <p className="text-gray-500">Absent</p>
                  </div>
                </div>
              </div>

              {/* Sticky Mobile / Bottom Save Button */}
              <div className="pt-2 sticky bottom-0 bg-white">
                <button
                  onClick={handleSaveAttendance}
                  disabled={loading}
                  className="w-full min-h-[48px] flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 active:bg-green-800 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-md text-sm sm:text-base"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <CheckSquare size={18} />
                      <span>Save Meeting & Attendance</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

